import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { Input } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyNetworkIcon } from '@root/entities/currency/ui'
import { Network } from '@root/entities/network'
import { useTranslation } from '@root/shared/localization'

import { TradeSettingsFormSolAutoFee } from './solana/trade-settings-sol-auto-fee'
import { TradeSettingsFormMev } from './trade-settings-form-mev'
import { TradeSettingsFormSlippage } from './trade-settings-form-slippage'
import { TradeSettingsFormSolRpc } from './trade-settings-form-sol-rpc'

import type { SvmValues } from '../../common'

const PRIORITY_FEE_MIN = 0.0001
const PRIORITY_FEE_MAX = 0.02
const JITO_TIP_MIN = 0.0002
const JITO_TIP_MAX = 0.02

type Props = {
  preset: number
}

export const TradeSettingsFormSol: React.FC<Props> = ({ preset }) => {
  const { t } = useTranslation()
  const { control, watch } = useFormContext<SvmValues>()
  const autoFees = watch('autoFee')

  const solIcon = <CurrencyNetworkIcon network={Network.Solana} size={24} />

  const validatePriorityFee = (str: string) => {
    const value = Number.parseFloat(str)
    if (value < PRIORITY_FEE_MIN) {
      return t('trade_settings.priority_fee_max_message', {
        max: PRIORITY_FEE_MIN,
      })
    }
    if (value > PRIORITY_FEE_MAX) {
      return t('trade_settings.priority_fee_max_message', {
        max: PRIORITY_FEE_MAX,
      })
    }
  }

  const validateJitoTip = (str: string) => {
    const value = Number.parseFloat(str)
    if (value < JITO_TIP_MIN) {
      return t('trade_settings.jito_tip_max_message', {
        max: JITO_TIP_MIN,
      })
    }
    if (value > JITO_TIP_MAX) {
      return t('trade_settings.jito_tip_max_message', {
        max: JITO_TIP_MAX,
      })
    }
  }

  return (
    <Root>
      <SenderSection>
        <TradeSettingsFormSolRpc key={preset} preset={preset} />
      </SenderSection>
      <SlippageSection>
        <TradeSettingsFormSlippage />
      </SlippageSection>
      <PrioritySection>
        <FieldHeaders>
          <FieldHeader>
            <FieldLabel>{t('trade_settings.priority_fee')}</FieldLabel>
            {/* <MaxLabel>Max 2 SOL</MaxLabel> */}
          </FieldHeader>
          <FieldHeader>
            <FieldLabel>{t('trade_settings.jito_tip')}</FieldLabel>
            {/* <MaxLabel>Max 2 SOL</MaxLabel> */}
          </FieldHeader>
        </FieldHeaders>

        <FieldInputs>
          <Controller
            control={control}
            name="priorityFee"
            rules={{
              required: t('validation.required'),
              validate: validatePriorityFee,
            }}
            render={({ field, fieldState }) => (
              <Input
                numeric
                allowLeadingZeros={false}
                decimalScale={CurrencyModel.Sol.decimals}
                disabled={autoFees}
                endContent={solIcon}
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="jitoTip"
            rules={{
              required: t('validation.required'),
              validate: validateJitoTip,
            }}
            render={({ field, fieldState }) => (
              <Input
                numeric
                allowLeadingZeros={false}
                decimalScale={CurrencyModel.Sol.decimals}
                disabled={autoFees}
                endContent={solIcon}
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
        </FieldInputs>
      </PrioritySection>

      <TradeSettingsFormSolAutoFee />
      <MevSection>
        <TradeSettingsFormMev antiMevName="jitoEnabled" />
      </MevSection>
    </Root>
  )
}

const Root = styled.div`
  display: grid;

  border-radius: 10px;
  border: 1px solid var(--backgroundA-3);
  background: var(--backgroundA-1);
  padding-inline: 14px;
  padding-block: 14px;
`

const PrioritySection = styled.div`
  padding-block: 10px 14px;
  display: grid;

  border-top: 1px solid var(--backgroundA-3);
`

const SenderSection = styled.div`
  padding-block: 0 14px;

  border-bottom: 1px solid var(--backgroundA-3);
`

const MevSection = styled.div`
  border-top: 1px solid var(--backgroundA-3);
`

const SlippageSection = styled.div`
  padding-block: 10px;

  display: grid;
  gap: 8px;
`

const FieldHeaders = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`

const FieldHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const FieldLabel = styled.span`
  color: var(--text-11);
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;

  margin-bottom: 6px;
`

const _MaxLabel = styled.span`
  color: var(--text-9);
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
`

const FieldInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`
